import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Save, Eye, Globe, Upload, Plus, Trash2,
  GripVertical, Image, Video, Music, Calendar, Mail,
  User, Layout, Palette, Settings, ExternalLink, Newspaper, Copy, Quote, Star,
  Briefcase, Users, HelpCircle, MessageSquare, Phone, CheckCircle2, XCircle, AlertTriangle, Link, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import CloudinaryUploader, { GalleryUploader } from '@/components/CloudinaryUploader';

export const NewsModuleEditor = ({ data, onChange, siteSlug }) => {
  const items = data.items || [];
  const layout = data.layout || 'grid';
  const spacing = data.spacing || 'spaced';
  const [editingIdx, setEditingIdx] = useState(null);
  
  const addItem = () => {
    const newItem = { 
      id: Date.now().toString(),
      title: 'New Article', 
      content: '', 
      image: '', 
      date: new Date().toISOString().split('T')[0],
      link: '' 
    };
    onChange({
      ...data, 
      items: [...items, newItem]
    });
    setEditingIdx(items.length); // Open editor for the new item
  };
  
  const updateItem = (idx, updates) => {
    const updated = [...items];
    updated[idx] = {...updated[idx], ...updates};
    onChange({...data, items: updated});
  };
  
  const removeItem = (idx) => {
    onChange({...data, items: items.filter((_, i) => i !== idx)});
    setEditingIdx(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Single item editor panel
  const renderEditor = (item, idx) => (
    <div className="bg-slate-800 border border-slate-600 rounded-lg p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-white">Edit News Item</h4>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setEditingIdx(null)}
          className="text-slate-400 hover:text-white"
        >
          Done
        </Button>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Title</Label>
          <Input 
            value={item.title} 
            onChange={(e) => updateItem(idx, { title: e.target.value })}
            placeholder="News headline"
          />
        </div>
        <div className="space-y-2">
          <Label>Date</Label>
          <Input 
            type="date"
            value={item.date || ''} 
            onChange={(e) => updateItem(idx, { date: e.target.value })}
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Content</Label>
        <Textarea 
          value={item.content} 
          onChange={(e) => updateItem(idx, { content: e.target.value })}
          placeholder="Write your news content..."
          rows={4}
        />
      </div>
      
      <div className="space-y-2">
        <Label>Featured Image</Label>
        <CloudinaryUploader
          currentImage={item.image}
          onUpload={(url) => updateItem(idx, { image: url })}
          onRemove={() => updateItem(idx, { image: '' })}
          folder={`sites/${siteSlug}/news`}
          aspectRatio="aspect-video"
          placeholder="Drop news image here"
        />
      </div>
      
      <div className="space-y-2">
        <Label>External Link (Optional)</Label>
        <Input 
          value={item.link || ''} 
          onChange={(e) => updateItem(idx, { link: e.target.value })}
          placeholder="https://..."
        />
      </div>

      <div className="pt-2 border-t border-slate-700">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => removeItem(idx)} 
          className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
        >
          <Trash2 className="w-4 h-4 mr-2" /> Delete Article
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Section Title</Label>
        <Input 
          value={data.title || 'News'} 
          onChange={(e) => onChange({...data, title: e.target.value})}
        />
      </div>

      {/* Layout Options */}
      <div className="space-y-3">
        <Label>Display Layout</Label>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onChange({...data, layout: 'list'})}
            className={cn(
              "flex flex-col items-center gap-1 px-4 py-3 rounded-lg border transition-all",
              layout === 'list' 
                ? "bg-orange-500/20 border-orange-500 text-orange-400" 
                : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500"
            )}
            title="List view"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <span className="text-xs">List</span>
          </button>
          <button
            onClick={() => onChange({...data, layout: 'grid'})}
            className={cn(
              "flex flex-col items-center gap-1 px-4 py-3 rounded-lg border transition-all",
              layout === 'grid' 
                ? "bg-orange-500/20 border-orange-500 text-orange-400" 
                : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500"
            )}
            title="Grid view"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            <span className="text-xs">Grid</span>
          </button>
          <button
            onClick={() => onChange({...data, layout: 'bento'})}
            className={cn(
              "flex flex-col items-center gap-1 px-4 py-3 rounded-lg border transition-all",
              layout === 'bento' 
                ? "bg-orange-500/20 border-orange-500 text-orange-400" 
                : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500"
            )}
            title="Bento grid (featured first)"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM14 12a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1v-2zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 18a1 1 0 011-1h4a1 1 0 011 1v1a1 1 0 01-1 1h-4a1 1 0 01-1-1v-1z" />
            </svg>
            <span className="text-xs">Bento</span>
          </button>
          
          <div className="h-8 w-px bg-slate-600 mx-2" />
          
          <button
            onClick={() => onChange({...data, spacing: 'spaced'})}
            className={cn(
              "flex flex-col items-center gap-1 px-4 py-3 rounded-lg border transition-all",
              spacing === 'spaced' 
                ? "bg-emerald-500/20 border-emerald-500 text-emerald-400" 
                : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500"
            )}
            title="Spaced items with gaps"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h4M14 6h4M4 12h4M14 12h4M4 18h4M14 18h4" />
            </svg>
            <span className="text-xs">Spaced</span>
          </button>
          <button
            onClick={() => onChange({...data, spacing: 'flush'})}
            className={cn(
              "flex flex-col items-center gap-1 px-4 py-3 rounded-lg border transition-all",
              spacing === 'flush' 
                ? "bg-emerald-500/20 border-emerald-500 text-emerald-400" 
                : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500"
            )}
            title="Flush items (no gaps)"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <span className="text-xs">Flush</span>
          </button>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>News Items ({items.length})</Label>
          <Button variant="outline" size="sm" onClick={addItem}>
            <Plus className="w-4 h-4 mr-1" /> Add News
          </Button>
        </div>
        
        {items.length === 0 ? (
          <div className="text-center py-12 bg-slate-700/30 rounded-lg border border-slate-600">
            <Newspaper className="w-10 h-10 mx-auto text-slate-500 mb-3" />
            <p className="text-sm text-slate-400">No news items yet. Add your first update!</p>
          </div>
        ) : editingIdx !== null && items[editingIdx] ? (
          // Show editor for selected item
          renderEditor(items[editingIdx], editingIdx)
        ) : (
          // Show clean list view
          <div className="space-y-2">
            {items.map((item, idx) => (
              <div 
                key={item.id || idx} 
                onClick={() => setEditingIdx(idx)}
                className="flex items-center gap-4 p-3 bg-slate-700/30 border border-slate-600 rounded-lg cursor-pointer hover:bg-slate-700/50 hover:border-slate-500 transition-all group"
              >
                {/* Thumbnail */}
                <div className="w-16 h-12 rounded bg-slate-600 overflow-hidden flex-shrink-0">
                  {item.image ? (
                    <img src={item.image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Newspaper className="w-5 h-5 text-slate-500" />
                    </div>
                  )}
                </div>
                
                {/* Title & Date */}
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-white truncate">{item.title || 'Untitled'}</h4>
                  <p className="text-xs text-slate-400">{formatDate(item.date)}</p>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setEditingIdx(idx); }}
                    className="text-slate-400 hover:text-white h-8 w-8 p-0"
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); removeItem(idx); }}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/20 h-8 w-8 p-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Events Module Editor

